'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { pick, mapObject } from 'underscore';
import SeamlessImmutable from 'seamless-immutable';
import { combineReducers } from 'redux';
import { sortBy } from 'underscore';
import undoable, { includeAction, combineFilters } from 'redux-undo';
import CustomWidgetBuffer from 'DesignData/models/CustomWidgetBuffer';
import CustomWidgetTranslations from 'DesignData/models/CustomWidgetTranslations';
import * as actions from 'DesignData/actions/ActionTypes';
import * as SourceErrorTypes from 'DesignData/enums/SourceErrorTypes';
import * as FieldErrorTypes from 'DesignData/enums/CustomWidgetFieldErrorTypes';
import * as FieldWarningTypes from 'DesignData/enums/CustomWidgetFieldWarningTypes';
import * as ModuleErrorTypes from 'DesignData/enums/CustomWidgetErrorTypes';
import * as SeverityTypes from 'DesignData/enums/CustomWidgetErrorSeverityTypes';
import * as fieldsHelpers from 'DesignData/helpers/customWidgetFieldsHelper';
import { CUSTOM_WIDGET } from 'DesignData/constants/FolderAssetTypes';
import { isBadRequestError, getErrorData } from 'DesignData/helpers/errorHelper';
import { getHublInCssOrJsWarnings, createSourceError } from 'DesignData/helpers/editorErrorHelper';
import { updateSmartRules } from 'smart-content-lib/utils/smartRules';
import { HISTORY_LIMIT } from 'DesignData/constants/UndoRedo';
import { EMPTY_OBJECT } from 'DesignData/lib/object';
const EMPTY_TRANSLATIONS = SeamlessImmutable({
  messages: {},
  masterLanguage: null
});

function getCustomWidgetBuffer(customWidget) {
  return new CustomWidgetBuffer(customWidget).without('fields');
}

function getCustomWidgetTranslations(customWidget) {
  const {
    messages,
    masterLanguage
  } = customWidget;
  if (!masterLanguage) return EMPTY_TRANSLATIONS;
  return new CustomWidgetTranslations({
    messages,
    masterLanguage
  });
}

function getSourceErrorType(error) {
  const {
    category = []
  } = error.errorTokens;
  return category[0] === SourceErrorTypes.UNKNOWN ? error.errorType : category[0];
}

function setChangeInfo(state, action, isBuffered) {
  const {
    customWidget
  } = action;

  if (!state || !customWidget) {
    return state;
  }

  return state.merge({
    isBuffered,
    updated: customWidget.updated
  });
}

function getAttributeErrorList(state, fieldId, attribute) {
  const fieldErrors = state[fieldId] || {};
  return fieldErrors[attribute] || [];
}

function getModuleAttributeErrorList(state, attribute) {
  return state[attribute] || [];
}

function translations(state = null, action) {
  switch (action.type) {
    case actions.CUSTOM_WIDGET_BUFFER_FETCH:
    case actions.CUSTOM_WIDGET_BUFFER_FETCH_BY_PATH:
    case actions.CUSTOM_WIDGET_BUFFER_UPGRADE:
    case actions.CUSTOM_WIDGET_REVERT:
    case actions.CUSTOM_WIDGET_BUFFER_DISCARD:
      {
        if (!action.customWidget) {
          return state;
        }

        return getCustomWidgetTranslations(action.customWidget);
      }

    case actions.CUSTOM_WIDGET_BUFFER_TRANSLATIONS_OPT_IN:
      {
        return state && state.set('masterLanguage', action.langCode).setIn(['messages', action.langCode], action.translations);
      }

    case actions.CUSTOM_WIDGET_BUFFER_ADD_LANGUAGE:
    case actions.CUSTOM_WIDGET_BUFFER_UPDATE_LANGUAGE:
      {
        const {
          langCode,
          translations: __translations
        } = action;

        if (!(langCode && __translations)) {
          return state;
        }

        return state.setIn(['messages', langCode], __translations);
      }

    case actions.CUSTOM_WIDGET_BUFFER_DELETE_LANGUAGE:
      {
        const {
          langCode
        } = action;

        if (!langCode) {
          return state;
        }

        return state.set('messages', state.messages.without(langCode));
      }

    case actions.CUSTOM_WIDGET_BUFFER_CHANGE_SOURCE_LANGUAGE:
      {
        const {
          langCode,
          translations: __translations
        } = action;

        if (!(langCode && __translations)) {
          return state;
        }

        const currentMasterLanguage = state.masterLanguage;
        return state.merge({
          masterLanguage: langCode,
          messages: state.messages.without(currentMasterLanguage).set(langCode, __translations)
        });
      }

    case actions.CUSTOM_WIDGET_BUFFER_UPDATE_MESSAGES:
      {
        const {
          messages
        } = action;
        if (!messages) return state;
        return state.set('messages', messages);
      }

    case actions.CUSTOM_WIDGET_BUFFER_UPDATE_TRANSLATION:
      {
        const {
          messages,
          langCode
        } = action;
        if (!(messages && langCode)) return state;
        return state.setIn(['messages', langCode], state.messages[langCode].merge(messages, {
          deep: true
        }));
      }

    case actions.CUSTOM_WIDGET_BUFFER_REPLACE_TRANSLATION:
      {
        const {
          messages,
          langCode
        } = action;
        if (!(messages && langCode)) return state;
        return state.setIn(['messages', langCode], messages);
      }

    default:
      {
        return state;
      }
  }
}

function buffer(state = null, action) {
  switch (action.type) {
    case actions.CUSTOM_WIDGET_BUFFER_FETCH:
    case actions.CUSTOM_WIDGET_BUFFER_FETCH_BY_PATH:
    case actions.CUSTOM_WIDGET_BUFFER_UPGRADE:
      {
        if (!action.customWidget) {
          return state;
        }

        return getCustomWidgetBuffer(action.customWidget);
      }

    case actions.CUSTOM_WIDGET_REVERT:
      {
        if (!action.customWidget) {
          return state;
        }

        return getCustomWidgetBuffer(action.customWidget).set('isBuffered', true);
      }

    case actions.CUSTOM_WIDGET_BUFFER_DISCARD:
      {
        if (!action.customWidget) {
          return state;
        }

        return getCustomWidgetBuffer(action.customWidget).set('isBuffered', false);
      }

    case actions.CUSTOM_WIDGET_BUFFER_SAVE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE_BY_PATH:
    case actions.CUSTOM_WIDGET_SAVE_BY_PATH:
      {
        return setChangeInfo(state, action, true);
      }

    case actions.CUSTOM_WIDGET_BUFFER_FORCE_UPGRADE:
      {
        if (!(action.customWidget || action.error) || action.error && !isBadRequestError(action.error)) {
          return state;
        }

        if (action.customWidget) {
          const newState = getCustomWidgetBuffer(action.customWidget);
          return setChangeInfo(newState, action, true);
        }

        const newState = state && state.merge({
          schemaVersion: 2
        });
        return setChangeInfo(newState, action, true);
      }

    case actions.CUSTOM_WIDGET_BUFFER_PUBLISH:
      {
        return setChangeInfo(state, action, false);
      }

    case actions.CUSTOM_WIDGET_SOURCE_UPDATE:
      {
        const newSource = pick(action, ['source', 'css', 'js']);
        return state && state.merge(newSource);
      }

    case actions.FINDER_FILE_RENAME:
      {
        const {
          file,
          fileType
        } = action;

        if (fileType !== CUSTOM_WIDGET || !file || !state) {
          return state;
        }

        const {
          name,
          label,
          path
        } = file;
        return state.merge({
          name,
          label,
          path
        });
      }

    case actions.CUSTOM_WIDGET_BUFFER_ADD_ASSET:
      {
        const {
          assetsType,
          asset
        } = action;

        if (!(state && assetsType && asset)) {
          return state;
        }

        const currentAssets = state[assetsType] || [];
        return state.merge({
          [assetsType]: currentAssets.concat(asset)
        });
      }

    case actions.CUSTOM_WIDGET_BUFFER_UPDATE:
      {
        const {
          changes = {}
        } = action;

        if (!state) {
          return state;
        }

        return state.merge(changes);
      }

    case actions.CUSTOM_WIDGET_BUFFER_ADD_SMART_RULE:
      {
        const {
          smartObject
        } = action;

        if (!(state && smartObject)) {
          return state;
        }

        const smartRules = state.smartObjects || [];
        const newSmartRules = sortBy([...smartRules, smartObject], 'order');
        return state.set('smartObjects', newSmartRules);
      }

    case actions.CUSTOM_WIDGET_BUFFER_EDIT_SMART_RULE:
      {
        const {
          smartRuleId,
          edits
        } = action;

        if (!(state && smartRuleId)) {
          return state;
        }

        const smartRules = state.smartObjects || [];
        const newSmartRules = smartRules.map(rule => {
          if (rule.criterion_id !== smartRuleId) {
            return rule;
          }

          return Object.assign({}, rule, {}, edits);
        });
        return state.set('smartObjects', newSmartRules);
      }

    case actions.CUSTOM_WIDGET_BUFFER_REMOVE_SMART_RULE:
      {
        const {
          smartRuleId
        } = action;

        if (!(state && smartRuleId)) {
          return state;
        }

        const smartRules = state.smartObjects || [];
        const newSmartRules = smartRules.filter(rule => {
          return rule.criterion_id !== smartRuleId;
        });
        const newState = state.set('smartObjects', newSmartRules);

        if (newSmartRules.length > 0) {
          return newState;
        } else {
          return newState.without('definitionId', 'smartType');
        }
      }

    case actions.CUSTOM_WIDGET_BUFFER_REMOVE_SMART_RULES:
      {
        if (!state) {
          return state;
        }

        return state.without('definitionId', 'smartType').set('smartObjects', []);
      }

    case actions.CUSTOM_WIDGET_BUFFER_EDIT_SMART_RULES_ORDER:
      {
        const {
          smartObjects
        } = action;

        if (!(state && smartObjects)) {
          return state;
        }

        const smartRules = state.smartObjects || [];
        const smartRulesById = smartRules.reduce((hash, rule) => {
          hash[rule.criterion_id] = rule;
          return hash;
        }, {});
        const newSmartRules = smartObjects.map(smartObject => {
          const {
            criterion_id: smartRuleId
          } = smartObject;
          const existingSmartRule = smartRulesById[smartRuleId];

          if (!existingSmartRule) {
            return smartObject;
          }

          return Object.assign({}, existingSmartRule, {}, smartObject);
        });
        return state.set('smartObjects', sortBy(newSmartRules, 'order'));
      }

    case actions.CUSTOM_WIDGET_BUFFER_MAKE_SMART:
      {
        const {
          smartObjectData
        } = action;

        if (!(state && smartObjectData)) {
          return state;
        }

        return state.merge(smartObjectData);
      }

    case actions.CUSTOM_WIDGET_BUFFERS_UPDATE_SMART_RULES:
      {
        const {
          smartObjects,
          smartMetaData
        } = action;
        const {
          definitionId,
          smartType
        } = smartMetaData;
        if (!state) return state;
        return state.merge({
          definitionId,
          smartType,
          smartObjects: updateSmartRules(state.smartObjects, smartObjects)
        });
      }

    default:
      {
        return state;
      }
  }
}

function fields(state = null, action) {
  switch (action.type) {
    case actions.CUSTOM_WIDGET_BUFFER_DISCARD:
    case actions.CUSTOM_WIDGET_BUFFER_FETCH:
    case actions.CUSTOM_WIDGET_BUFFER_FETCH_BY_PATH:
    case actions.CUSTOM_WIDGET_BUFFER_UPGRADE:
    case actions.CUSTOM_WIDGET_REVERT:
      {
        if (!action.customWidget) {
          return state;
        }

        return fieldsHelpers.getNormalizedFields(action.customWidget.fields, true);
      }

    case actions.CUSTOM_WIDGET_FIELD_MOVE:
      {
        const {
          fieldId,
          replaceFieldId,
          insertType
        } = action;
        return fieldsHelpers.moveField(state, fieldId, replaceFieldId, insertType);
      }

    case actions.CUSTOM_WIDGET_FIELD_DELETE:
      {
        const {
          fieldId
        } = action;
        return fieldsHelpers.deleteField(state, fieldId);
      }

    case actions.CUSTOM_WIDGET_FIELD_EDIT:
      {
        const {
          field
        } = action;
        return state.merge({
          fields: fieldsHelpers.editField(state.fields, field)
        });
      }

    case actions.CUSTOM_WIDGET_FIELD_CREATE:
      {
        const {
          field,
          parentFieldId
        } = action;
        const newField = field.name ? field : field.merge({
          name: fieldsHelpers.getUniqueFieldName(field.type, state.fields)
        });
        return fieldsHelpers.createField(state, newField, parentFieldId);
      }

    case actions.CUSTOM_WIDGET_FIELD_CREATE_GROUP:
      {
        const {
          fieldGroup,
          children
        } = action;
        return fieldsHelpers.createFieldGroup(state, fieldGroup, children);
      }

    case actions.CUSTOM_WIDGET_FIELD_CLONE:
      {
        const {
          clonedField,
          parentFieldId,
          index
        } = action;
        return fieldsHelpers.createField(state, clonedField, parentFieldId, index);
      }

    case actions.CUSTOM_WIDGET_GLOBAL_CONTENT_EDIT:
      {
        const {
          globalContent
        } = action;
        return fieldsHelpers.editGlobalContent(state, globalContent);
      }

    default:
      {
        return state;
      }
  }
}

const UNDO_REDO_ACTIONS = includeAction([actions.CUSTOM_WIDGET_BUFFER_UPDATE, actions.CUSTOM_WIDGET_FIELD_MOVE, actions.CUSTOM_WIDGET_FIELD_DELETE, actions.CUSTOM_WIDGET_FIELD_EDIT, actions.CUSTOM_WIDGET_FIELD_CREATE, actions.CUSTOM_WIDGET_FIELD_CREATE_GROUP, actions.CUSTOM_WIDGET_FIELD_CLONE, actions.CUSTOM_WIDGET_GLOBAL_CONTENT_EDIT]);

function getBufferAndFieldsReducer(customWidgetId) {
  const onlyListenToSelf = filterAction => {
    return filterAction.customWidgetId === customWidgetId;
  };

  return undoable(combineReducers({
    buffer,
    fields,
    translations
  }), {
    limit: HISTORY_LIMIT,
    filter: combineFilters(UNDO_REDO_ACTIONS, onlyListenToSelf),
    undoType: actions.CUSTOM_WIDGET_EDIT_UNDO,
    redoType: actions.CUSTOM_WIDGET_EDIT_REDO
  });
}

function byId(state = EMPTY_OBJECT, action) {
  const {
    customWidgetId
  } = action;

  switch (action.type) {
    case actions.CUSTOM_WIDGET_BUFFER_FETCH:
    case actions.CUSTOM_WIDGET_BUFFER_FETCH_BY_PATH:
    case actions.CUSTOM_WIDGET_BUFFER_UPGRADE:
    case actions.CUSTOM_WIDGET_REVERT:
    case actions.CUSTOM_WIDGET_BUFFER_DISCARD:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE_BY_PATH:
    case actions.CUSTOM_WIDGET_BUFFER_FORCE_UPGRADE:
    case actions.CUSTOM_WIDGET_BUFFER_PUBLISH:
    case actions.CUSTOM_WIDGET_SOURCE_UPDATE:
    case actions.CUSTOM_WIDGET_FIELD_MOVE:
    case actions.CUSTOM_WIDGET_FIELD_CREATE:
    case actions.CUSTOM_WIDGET_FIELD_EDIT:
    case actions.CUSTOM_WIDGET_FIELD_DELETE:
    case actions.CUSTOM_WIDGET_FIELD_CREATE_GROUP:
    case actions.CUSTOM_WIDGET_FIELD_CLONE:
    case actions.CUSTOM_WIDGET_GLOBAL_CONTENT_EDIT:
    case actions.CUSTOM_WIDGET_EDIT_UNDO:
    case actions.CUSTOM_WIDGET_EDIT_REDO:
    case actions.CUSTOM_WIDGET_BUFFER_ADD_ASSET:
    case actions.CUSTOM_WIDGET_BUFFER_UPDATE:
    case actions.CUSTOM_WIDGET_BUFFER_TRANSLATIONS_OPT_IN:
    case actions.CUSTOM_WIDGET_BUFFER_ADD_LANGUAGE:
    case actions.CUSTOM_WIDGET_BUFFER_DELETE_LANGUAGE:
    case actions.CUSTOM_WIDGET_BUFFER_UPDATE_LANGUAGE:
    case actions.CUSTOM_WIDGET_BUFFER_CHANGE_SOURCE_LANGUAGE:
    case actions.CUSTOM_WIDGET_BUFFER_UPDATE_MESSAGES:
    case actions.CUSTOM_WIDGET_BUFFER_UPDATE_TRANSLATION:
    case actions.CUSTOM_WIDGET_BUFFER_REPLACE_TRANSLATION:
    case actions.CUSTOM_WIDGET_BUFFER_ADD_SMART_RULE:
    case actions.CUSTOM_WIDGET_BUFFER_REMOVE_SMART_RULES:
    case actions.CUSTOM_WIDGET_BUFFER_EDIT_SMART_RULE:
    case actions.CUSTOM_WIDGET_BUFFER_REMOVE_SMART_RULE:
    case actions.CUSTOM_WIDGET_BUFFER_EDIT_SMART_RULES_ORDER:
    case actions.CUSTOM_WIDGET_BUFFERS_UPDATE_SMART_RULES:
    case actions.CUSTOM_WIDGET_BUFFER_MAKE_SMART:
      {
        const bufferAndFields = getBufferAndFieldsReducer(customWidgetId);
        return state.set(customWidgetId, bufferAndFields(state[customWidgetId], action));
      }

    case actions.FINDER_FILE_RENAME:
      {
        const {
          fileId
        } = action;
        const bufferAndFields = getBufferAndFieldsReducer(fileId);
        return state.set(fileId, bufferAndFields(state[fileId], action));
      }

    case actions.CUSTOM_WIDGET_BUFFER_DELETE:
      {
        return state.without(customWidgetId);
      }

    default:
      {
        return state;
      }
  }
}

function isValidatingById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case actions.CUSTOM_WIDGET_BUFFER_VALIDATE:
      {
        const isValidating = !(action.isValid || action.error);
        return SeamlessImmutable.set(state, action.customWidgetId, isValidating);
      }

    default:
      {
        return state;
      }
  }
}

function isSavingById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case actions.CUSTOM_WIDGET_BUFFER_SAVE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE_BY_PATH:
    case actions.CUSTOM_WIDGET_BUFFER_FORCE_UPGRADE:
      {
        const {
          customWidgetId,
          customWidget,
          error
        } = action;
        return state.set(customWidgetId, !(customWidget || error));
      }

    default:
      {
        return state;
      }
  }
}

function saveErrorById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case actions.CUSTOM_WIDGET_BUFFER_VALIDATE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE_BY_PATH:
    case actions.CUSTOM_WIDGET_BUFFER_FORCE_UPGRADE:
      {
        return state.set(action.customWidgetId, action.error);
      }

    default:
      {
        return state;
      }
  }
}

function isPublishingById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case actions.CUSTOM_WIDGET_BUFFER_PUBLISH:
      {
        const {
          customWidgetId,
          customWidget,
          error
        } = action;
        return state.set(customWidgetId, !(customWidget || error));
      }

    default:
      {
        return state;
      }
  }
}

function upgradeErrorById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case actions.CUSTOM_WIDGET_BUFFER_UPGRADE:
      {
        return state.set(action.customWidgetId, action.error);
      }

    case actions.CUSTOM_WIDGET_BUFFER_FORCE_UPGRADE:
      {
        const error = isBadRequestError(action.error) ? null : action.error;
        return state.set(action.customWidgetId, error);
      }

    default:
      {
        return state;
      }
  }
}

function sourceErrors(state = SeamlessImmutable([]), action) {
  switch (action.type) {
    case actions.CUSTOM_WIDGET_BUFFER_VALIDATE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE_BY_PATH:
    case actions.CUSTOM_WIDGET_BUFFER_FORCE_UPGRADE:
      {
        const {
          error,
          customWidget
        } = action;
        const newState = SeamlessImmutable([]);

        if (customWidget) {
          return newState;
        }

        const errorData = getErrorData(error);

        if (!isBadRequestError(error) || !errorData) {
          return state;
        }

        const {
          errors = []
        } = errorData;
        const newErrors = errors.filter(_error => {
          const isFatalError = !_error.severity || _error.severity === SeverityTypes.FATAL;
          return !!SourceErrorTypes[getSourceErrorType(_error)] && isFatalError;
        }).map(_error => {
          return createSourceError(Object.assign({}, _error, {
            errorType: getSourceErrorType(_error)
          }));
        });
        return newState.concat(newErrors);
      }

    default:
      {
        return state;
      }
  }
}
/*
{
  [customWidgetId]: [
    {
      type: "DISABLED"
      line: 12
      data: {
        token: "footer_js()"
      }
    }
  ]
}
*/


function sourceErrorsById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case actions.CUSTOM_WIDGET_BUFFER_VALIDATE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE_BY_PATH:
    case actions.CUSTOM_WIDGET_BUFFER_FORCE_UPGRADE:
      {
        const {
          customWidgetId
        } = action;
        return state.set(customWidgetId, sourceErrors(state[customWidgetId], action));
      }

    default:
      {
        return state;
      }
  }
}

function cssErrors(state = SeamlessImmutable([]), action) {
  switch (action.type) {
    case actions.CUSTOM_WIDGET_BUFFER_VALIDATE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE_BY_PATH:
      {
        const {
          css
        } = action.customWidget || action.customWidgetData || {};

        if (!(css || css === '')) {
          return state;
        }

        return getHublInCssOrJsWarnings(css);
      }

    default:
      {
        return state;
      }
  }
}

function cssErrorsById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case actions.CUSTOM_WIDGET_BUFFER_VALIDATE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE_BY_PATH:
      {
        const {
          customWidgetId
        } = action;
        return state.set(customWidgetId, cssErrors(state[customWidgetId], action));
      }

    default:
      {
        return state;
      }
  }
}

function jsErrors(state = SeamlessImmutable([]), action) {
  switch (action.type) {
    case actions.CUSTOM_WIDGET_BUFFER_VALIDATE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE_BY_PATH:
      {
        const {
          js
        } = action.customWidget || action.customWidgetData || {};

        if (!(js || js === '')) {
          return state;
        }

        return getHublInCssOrJsWarnings(js);
      }

    default:
      {
        return state;
      }
  }
}

function jsErrorsById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case actions.CUSTOM_WIDGET_BUFFER_VALIDATE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE_BY_PATH:
      {
        const {
          customWidgetId
        } = action;
        return state.set(customWidgetId, jsErrors(state[customWidgetId], action));
      }

    default:
      {
        return state;
      }
  }
}

function fieldsErrors(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case actions.CUSTOM_WIDGET_BUFFER_VALIDATE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE_BY_PATH:
    case actions.CUSTOM_WIDGET_BUFFER_FORCE_UPGRADE:
      {
        const {
          error,
          customWidget
        } = action;
        let newState = EMPTY_OBJECT;

        if (customWidget) {
          return newState;
        }

        const errorData = getErrorData(error);

        if (!isBadRequestError(error) || !errorData) {
          return state;
        }

        const {
          errors = []
        } = errorData;
        errors.forEach(_error => {
          const isFatalError = !_error.severity || _error.severity === SeverityTypes.FATAL;

          if (!FieldErrorTypes[_error.errorType] || !isFatalError) {
            return;
          }

          const _ref = _error.errorTokens || {},
                {
            field = [],
            uiattribute = []
          } = _ref,
                rest = _objectWithoutPropertiesLoose(_ref, ["field", "uiattribute"]);

          if (!field.length) {
            return;
          }

          const errorList = getAttributeErrorList(newState, field[0], uiattribute[0]);
          const newError = {
            type: _error.errorType,
            data: mapObject(rest, v => v[0])
          };
          newState = newState.setIn([field[0], uiattribute[0]], errorList.concat([newError]));
        });
        return newState;
      }

    default:
      {
        return state;
      }
  }
}
/*
{
  [customWidgetId]: {
    [fieldId]: {
      name: [
        {type: NO_WHITESPACE},
        {type: NO_PUNCTUATION}
      ],
      min: [
        {type: NUMBER_MIN_GREATER_THAN_MAX, data: {min: 10, max: 0}}
      ],
    }
  }
}
*/


function fieldsErrorsById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case actions.CUSTOM_WIDGET_BUFFER_VALIDATE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE_BY_PATH:
    case actions.CUSTOM_WIDGET_BUFFER_FORCE_UPGRADE:
      {
        const {
          customWidgetId
        } = action;
        return state.set(customWidgetId, fieldsErrors(state[customWidgetId], action));
      }

    default:
      {
        return state;
      }
  }
}

function fieldWarnings(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case actions.CUSTOM_WIDGET_BUFFER_VALIDATE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE_BY_PATH:
    case actions.CUSTOM_WIDGET_BUFFER_FORCE_UPGRADE:
      {
        const {
          error,
          warnings,
          customWidget = {}
        } = action;
        const errorData = getErrorData(error);
        let _warnings = [];

        if (warnings && warnings.length) {
          _warnings = [...warnings];
        } else if (customWidget.warnings && customWidget.warnings.length) {
          _warnings = [...customWidget.warnings];
        } else if (errorData || isBadRequestError(error)) {
          const {
            errors = []
          } = errorData;
          _warnings = [...errors];
        }

        let newState = EMPTY_OBJECT;

        _warnings.forEach(warning => {
          if (warning.severity && warning.severity === SeverityTypes.WARNING && !!FieldWarningTypes[warning.errorType]) {
            const _ref2 = warning.errorTokens || {},
                  {
              field = [],
              uiattribute = []
            } = _ref2,
                  rest = _objectWithoutPropertiesLoose(_ref2, ["field", "uiattribute"]);

            if (!field.length) return;
            const warningList = getAttributeErrorList(newState, field[0], uiattribute[0]);
            const newWarning = {
              type: warning.errorType,
              data: mapObject(rest, v => v[0])
            };
            newState = newState.setIn([field[0], uiattribute[0]], warningList.concat([newWarning]));
          }
        });

        return newState;
      }

    default:
      {
        return state;
      }
  }
}

function fieldsWarningsById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case actions.CUSTOM_WIDGET_BUFFER_VALIDATE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE_BY_PATH:
    case actions.CUSTOM_WIDGET_BUFFER_FORCE_UPGRADE:
      {
        const {
          customWidgetId
        } = action;
        return state.set(customWidgetId, fieldWarnings(state[customWidgetId], action));
      }

    default:
      {
        return state;
      }
  }
}

function moduleErrors(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case actions.CUSTOM_WIDGET_BUFFER_VALIDATE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE_BY_PATH:
      {
        const {
          error,
          customWidget
        } = action;
        let newState = EMPTY_OBJECT;

        if (customWidget) {
          return newState;
        }

        const errorData = getErrorData(error);

        if (!isBadRequestError(error) || !errorData) {
          return state;
        }

        const {
          errors = []
        } = errorData;
        errors.forEach(_error => {
          const isFatalError = !_error.severity || _error.severity === SeverityTypes.FATAL;

          if (!ModuleErrorTypes[_error.errorType] || !isFatalError) {
            return;
          }

          const _ref3 = _error.errorTokens || {},
                {
            uiattribute
          } = _ref3,
                rest = _objectWithoutPropertiesLoose(_ref3, ["uiattribute"]);

          const newError = {
            type: _error.errorType,
            data: mapObject(rest, v => v[0])
          };
          const moduleProperty = uiattribute[0];
          newState = newState.set(moduleProperty, getModuleAttributeErrorList(newState, moduleProperty).concat(newError));
        });
        return newState;
      }

    default:
      {
        return state;
      }
  }
}

function moduleErrorsById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case actions.CUSTOM_WIDGET_BUFFER_VALIDATE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE:
    case actions.CUSTOM_WIDGET_BUFFER_SAVE_BY_PATH:
      {
        const {
          customWidgetId
        } = action;
        return state.set(customWidgetId, moduleErrors(state[customWidgetId], action));
      }

    default:
      {
        return state;
      }
  }
}

function fetchErrorById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case actions.CUSTOM_WIDGET_BUFFER_FETCH:
    case actions.CUSTOM_WIDGET_BUFFER_FETCH_BY_PATH:
      {
        const {
          customWidgetId,
          error
        } = action;
        return error ? state.set(customWidgetId, error) : state.without(customWidgetId);
      }

    default:
      {
        return state;
      }
  }
}

export default combineReducers({
  byId,
  isSavingById,
  isValidatingById,
  saveErrorById,
  isPublishingById,
  moduleErrorsById,
  fieldsErrorsById,
  sourceErrorsById,
  cssErrorsById,
  jsErrorsById,
  upgradeErrorById,
  fetchErrorById,
  fieldsWarningsById
});